/**
 * Property Attribute Utilities
 *
 * Helper functions for determining which property attributes to display
 * based on property type and available values.
 */

import {
  getPropertyTypeConfig,
  getPropertyCategory,
  isLandProperty,
  shouldShowFloorInfo,
  type AttributeKey,
} from '../config/propertyTypeConfig';
import { formatAmount } from './numberFormat';

export interface PropertyData {
  propertyType: string;
  bedrooms?: number;
  bathrooms?: number;
  sqft?: number;
  area?: number;
  parking?: number | boolean;
  yearBuilt?: number;
  floors?: number;
  furnished?: boolean;
  isFurnished?: boolean;
  petFriendly?: boolean;
  isPetFriendly?: boolean;
  orientation?: string;
  floorLevel?: number;
  unitNumber?: string;
  apartmentNumber?: string;
  livingRooms?: number;
}

export interface DisplayAttribute {
  key: AttributeKey;
  value: string | number | boolean;
  label: string;
  labelAr: string;
  icon: string;
  isPrimary: boolean;
}

/**
 * Normalize property type to uppercase for consistent lookup
 */
function normalizePropertyType(type: string): string {
  if (!type) return 'APARTMENT';
  // Handle common variations
  const normalized = type.toUpperCase().replace(/\s+/g, '_');
  return normalized;
}

/**
 * Get the display label for an attribute based on property type
 */
export function getAttributeLabel(
  propertyType: string,
  attributeKey: AttributeKey,
  lang: 'en' | 'ar' = 'en'
): string {
  const config = getPropertyTypeConfig(propertyType);
  const attrConfig = config.attributes[attributeKey];

  if (!attrConfig) {
    // Fallback labels
    const fallbacks: Record<AttributeKey, { en: string; ar: string }> = {
      bedrooms: { en: 'Bedrooms', ar: 'غرف النوم' },
      bathrooms: { en: 'Bathrooms', ar: 'دورات المياه' },
      area: { en: 'Area', ar: 'المساحة' },
      parking: { en: 'Parking', ar: 'مواقف' },
      yearBuilt: { en: 'Year Built', ar: 'سنة البناء' },
      floors: { en: 'Floors', ar: 'الطوابق' },
      furnished: { en: 'Furnished', ar: 'مفروشة' },
      petFriendly: { en: 'Pet Friendly', ar: 'يسمح بالحيوانات' },
      orientation: { en: 'Orientation', ar: 'الواجهة' },
      floorLevel: { en: 'Floor', ar: 'الدور' },
      unitNumber: { en: 'Unit', ar: 'رقم الوحدة' },
      livingRooms: { en: 'Living Rooms', ar: 'غرف المعيشة' },
    };
    return lang === 'ar' ? fallbacks[attributeKey]?.ar : fallbacks[attributeKey]?.en;
  }

  return lang === 'ar' ? attrConfig.labelKeyAr : attrConfig.labelKey;
}

/**
 * Check if a specific attribute should be shown for a property type
 */
export function shouldShowAttribute(
  propertyType: string,
  attributeKey: AttributeKey,
  value: unknown
): boolean {
  const config = getPropertyTypeConfig(propertyType);
  const attrConfig = config.attributes[attributeKey];

  // If attribute not configured for this type, don't show
  if (!attrConfig || !attrConfig.show) {
    return false;
  }

  // Hide zero/null/undefined values if configured
  if (config.hideZeroValues) {
    if (value === null || value === undefined) return false;
    if (typeof value === 'number' && value === 0) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
  }

  return true;
}

/**
 * Extract the attribute value from property data
 */
function getAttributeValue(property: PropertyData, key: AttributeKey): unknown {
  switch (key) {
    case 'bedrooms':
      return property.bedrooms;
    case 'bathrooms':
      return property.bathrooms;
    case 'area':
      return property.sqft || property.area;
    case 'parking':
      return property.parking;
    case 'yearBuilt':
      return property.yearBuilt;
    case 'floors':
      return property.floors;
    case 'furnished':
      return property.furnished ?? property.isFurnished;
    case 'petFriendly':
      return property.petFriendly ?? property.isPetFriendly;
    case 'orientation':
      return property.orientation;
    case 'floorLevel':
      return property.floorLevel;
    case 'unitNumber':
      return property.unitNumber || property.apartmentNumber;
    case 'livingRooms':
      return property.livingRooms;
    default:
      return undefined;
  }
}

/**
 * Get all displayable attributes for a property
 * Returns only attributes that are relevant to the property type and have values
 */
export function getPropertyDisplayAttributes(
  property: PropertyData,
  lang: 'en' | 'ar' = 'en'
): DisplayAttribute[] {
  const propertyType = normalizePropertyType(property.propertyType);
  const config = getPropertyTypeConfig(propertyType);
  const displayAttributes: DisplayAttribute[] = [];

  // All possible attributes to check
  const allAttributes: AttributeKey[] = [
    'bedrooms',
    'bathrooms',
    'area',
    'parking',
    'yearBuilt',
    'floors',
    'furnished',
    'petFriendly',
    'orientation',
    'floorLevel',
    'unitNumber',
    'livingRooms',
  ];

  for (const key of allAttributes) {
    const value = getAttributeValue(property, key);
    const attrConfig = config.attributes[key];

    if (shouldShowAttribute(propertyType, key, value) && attrConfig) {
      displayAttributes.push({
        key,
        value: value as string | number | boolean,
        label: lang === 'ar' ? attrConfig.labelKeyAr : attrConfig.labelKey,
        labelAr: attrConfig.labelKeyAr,
        icon: attrConfig.icon,
        isPrimary: config.primaryAttributes.includes(key),
      });
    }
  }

  // Sort: primary attributes first, then others
  displayAttributes.sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;
    return 0;
  });

  return displayAttributes;
}

/**
 * Get primary display attributes for property card (compact view)
 * Returns top 4 most relevant attributes for the property type
 */
export function getPrimaryDisplayAttributes(
  property: PropertyData,
  lang: 'en' | 'ar' = 'en',
  maxCount: number = 4
): DisplayAttribute[] {
  const allAttributes = getPropertyDisplayAttributes(property, lang);
  return allAttributes.slice(0, maxCount);
}

/**
 * Format attribute value for display
 */
export function formatAttributeValue(
  key: AttributeKey,
  value: unknown,
  lang: 'en' | 'ar' = 'en'
): string {
  if (value === null || value === undefined) return '';

  switch (key) {
    case 'area': {
      const areaNum = typeof value === 'number' ? value : parseFloat(String(value));
      if (isNaN(areaNum)) return '';
      const formatted = formatAmount(areaNum);
      return lang === 'ar' ? `${formatted} م²` : `${formatted} sqft`;
    }

    case 'furnished':
    case 'petFriendly':
      if (typeof value === 'boolean') {
        if (key === 'furnished') {
          return value ? (lang === 'ar' ? 'نعم' : 'Yes') : (lang === 'ar' ? 'لا' : 'No');
        }
        if (key === 'petFriendly') {
          return value ? (lang === 'ar' ? 'نعم' : 'Yes') : (lang === 'ar' ? 'لا' : 'No');
        }
      }
      return String(value);

    case 'parking':
      if (typeof value === 'boolean') {
        return value ? (lang === 'ar' ? 'متوفر' : 'Available') : '';
      }
      return String(value);

    case 'orientation': {
      // Orientation values might need translation
      const orientations: Record<string, { en: string; ar: string }> = {
        NORTH: { en: 'North', ar: 'شمال' },
        SOUTH: { en: 'South', ar: 'جنوب' },
        EAST: { en: 'East', ar: 'شرق' },
        WEST: { en: 'West', ar: 'غرب' },
        NORTHEAST: { en: 'Northeast', ar: 'شمال شرق' },
        NORTHWEST: { en: 'Northwest', ar: 'شمال غرب' },
        SOUTHEAST: { en: 'Southeast', ar: 'جنوب شرق' },
        SOUTHWEST: { en: 'Southwest', ar: 'جنوب غرب' },
      };
      const orientKey = String(value).toUpperCase();
      return orientations[orientKey]?.[lang] || String(value);
    }

    default:
      return String(value);
  }
}

// Re-export useful functions from config
export { getPropertyCategory, isLandProperty, shouldShowFloorInfo };
